import { Component } from '@verndale/core';

// Fallbacks only — the host overrides each of these with a data attribute.
// The toast copy is authored content; the other two are integration details the
// host can retune without a FEE rebuild.
const DEFAULTS = {
  savedMessage: 'Saved to Your Academy Favorites',
  // After an anonymous user is bounced through login, the host returns them to
  // the page with ?save=true so we can finish the save they asked for.
  pendingSaveParam: 'save',
  // Field the host returns the login URL in on a 401.
  loginUrlField: 'redirectUrl'
};

class ContentActions extends Component {
  setupDefaults() {
    this.dom = {
      save: this.el.querySelector('[data-save]'),
      login: this.el.querySelector('[data-login]'),
      nativeShare: this.el.querySelector('[data-native-share]')
    };

    const {
      bookmarkEndpoint,
      loginUrl,
      loginUrlField,
      pageId,
      pendingSaveParam,
      saved,
      savedMessage,
      shareUrl,
      shareTitle
    } = this.el.dataset;

    this.bookmarkEndpoint = bookmarkEndpoint || '';
    this.pageId = pageId || '';
    this.shareUrl = shareUrl || window.location.href;
    this.shareTitle = shareTitle || document.title;
    this.savedMessage = savedMessage || DEFAULTS.savedMessage;
    this.pendingSaveParam = pendingSaveParam || DEFAULTS.pendingSaveParam;
    this.loginUrlField = loginUrlField || DEFAULTS.loginUrlField;
    this.pending = false;

    // The host may hang data-login-url off either the wrapper or the button;
    // the button wins so a per-control URL can override the block-level one.
    this.loginUrl = this.dom.login?.dataset.loginUrl || loginUrl || '';

    // `data-saved` on the wrapper is the render-time state (set by the host from
    // the member's profile). Falls back to the button markup when it's absent.
    this.saved =
      saved !== undefined
        ? saved === 'true'
        : this.dom.save?.getAttribute('aria-pressed') === 'true';

    // Suppresses the login redirect while finishing a post-login save — a second
    // 401 there means the session still isn't valid, and redirecting would loop.
    this.resumingPendingSave = false;

    this.setSavedState(this.saved);
  }

  addListeners() {
    this.dom.save?.addEventListener('click', this.handleSave.bind(this));
    this.dom.login?.addEventListener('click', this.handleLogin.bind(this));
    this.dom.nativeShare?.addEventListener(
      'click',
      this.handleNativeShare.bind(this)
    );

    this.resumePendingSave();
  }

  async handleSave() {
    if (this.pending) return;

    const nextSaved = !this.saved;

    // Persist first — only reflect the new state once the API confirms it.
    if (this.bookmarkEndpoint && !(await this.persistBookmark(nextSaved))) {
      return;
    }

    this.applySavedState(nextSaved);
  }

  handleLogin() {
    this.el.dispatchEvent(
      new CustomEvent('content-actions:login-required', {
        bubbles: true,
        detail: { pageId: this.pageId, url: this.shareUrl }
      })
    );

    if (this.loginUrl) {
      window.location.assign(this.loginUrl);
    }
  }

  /**
   * Finishes a save the user requested before logging in.
   */
  async resumePendingSave() {
    const canPersist = Boolean(this.dom.save && this.bookmarkEndpoint);

    // Share-only pages (eatright.org has no login) must leave the URL alone —
    // the flag isn't ours to consume there.
    if (!canPersist && !this.dom.login) return;

    const params = new URLSearchParams(window.location.search);

    if (params.get(this.pendingSaveParam) !== 'true') return;

    // Drop the flag before the request so a refresh — or a retry that fails —
    // can't fire the save a second time.
    params.delete(this.pendingSaveParam);
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${
        window.location.hash
      }`
    );

    // Still logged out: the flag is spent, but there's nothing to persist.
    if (!canPersist || this.saved) return;

    // A pending save is always an add — the user clicked Save, not Remove.
    this.resumingPendingSave = true;
    const persisted = await this.persistBookmark(true);
    this.resumingPendingSave = false;

    if (persisted) {
      this.applySavedState(true);
    }
  }

  applySavedState(saved) {
    this.setSavedState(saved);

    if (saved) {
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: this.savedMessage, iconId: 'bookmark-fill' }
        })
      );
    }

    this.el.dispatchEvent(
      new CustomEvent('content-actions:save', {
        bubbles: true,
        detail: { saved, pageId: this.pageId, url: this.shareUrl }
      })
    );
  }

  async persistBookmark(nextSaved) {
    this.setPending(true);

    try {
      const response = await fetch(this.bookmarkEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ pageId: this.pageId, bookmarked: nextSaved })
      });

      // Not signed in. The host answers with the URL to send the user to; we
      // redirect, and it returns them here with ?save=true to finish the save.
      if (response.status === 401 || response.status === 403) {
        this.el.dispatchEvent(
          new CustomEvent('content-actions:login-required', {
            bubbles: true,
            detail: { pageId: this.pageId, url: this.shareUrl }
          })
        );

        // Already came back from login and still unauthorised — redirecting
        // again would bounce the user in a loop.
        if (!this.resumingPendingSave) {
          await this.redirectToLogin(response);
        }

        return false;
      }

      if (!response.ok) {
        throw new Error(`Bookmark request failed: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      this.setPending(false);
    }
  }

  /**
   * Sends the user to the host's login page, which returns them here with
   * ?save=true so the save they asked for can be completed.
   */
  async redirectToLogin(response) {
    let loginUrl = '';

    try {
      const payload = await response.json();
      loginUrl = payload?.[this.loginUrlField] || '';
    } catch (error) {
      // No JSON body on the 401 — fall back to the render-time URL below.
    }

    loginUrl = loginUrl || this.loginUrl;

    if (loginUrl) {
      window.location.assign(loginUrl);
    }
  }

  setPending(pending) {
    this.pending = pending;

    if (!this.dom.save) return;

    this.dom.save.disabled = pending;
    this.dom.save.setAttribute('aria-busy', String(pending));
  }

  setSavedState(saved) {
    this.saved = saved;

    if (!this.dom.save) return;

    this.dom.save.setAttribute('aria-pressed', String(saved));
    this.dom.save.classList.toggle('is-saved', saved);
  }

  async handleNativeShare() {
    const shareData = { title: this.shareTitle, url: this.shareUrl };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User dismissed the share sheet — no action needed.
      }
      return;
    }

    // No native share support: fall back to the inline social links.
    this.el.classList.add('content-actions--fallback');
  }
}

export default ContentActions;
